import React from 'react';
import { useDocumentTypeItem } from '../../../hooks/useDocumentTypeItem';
import { isDocumentExpired, getRecurrenceText } from '../../../utils/documentUtils';
import { DocumentType } from '../../../types/Document';
import DocumentUploadForm from './DocumentUploadForm';
import DocumentPeriodSelector from './DocumentPeriodSelector';
import DocumentViewer from './DocumentViewer';
import styles from '../../../styles/DocumentManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { useDocumentContext } from 'contexts/DocumentContext';

interface DocumentTypeItemProps {
  type: DocumentType;
  isCompanyMode?: boolean;
}

const DocumentTypeItem: React.FC<DocumentTypeItemProps> = React.memo(({
  type,
  isCompanyMode = false,
}) => {
  const [inputEnabled, setInputEnabled] = React.useState(true);
  const { selectedUser } = useDocumentContext();
  const [removingId, setRemovingId] = React.useState<string | null>(null);

  const {
    showUploadForm,
    processing,
    isHighlighted,
    isFading,
    reminderJustSent,
    reminderSentToday,
    containerRef,

    // Period data
    periods,
    selectedPeriod,
    selectedPeriodKey,
    template,

    // Context data
    error,
    documentDownload,

    // Handlers
    handleUploadFormData,
    handleRemoveDocument,
    handlePeriodChange,
    handleSendReminder,
    handleTemplateDownload,
    setShowUploadForm,

    // Permission/UI flags
    canEdit,
    showReminderIndicator,
    showSendReminderButton,
    showReminderSentIndicator,
    showUploadButton,
    isCompanyMode: companyMode,
  } = useDocumentTypeItem({ type, isCompanyMode });

  const handleCheck = () => {
    setInputEnabled(v => !v);
    setShowUploadForm(false);
  };

  if (error) {
    return <div className={styles.inlineError}>Error: {error}</div>;
  }

  const isMulti = type.multi_uploads;

  // For non-multi: the single document in the selected period (or null)
  const selectedDocument = isMulti ? null : (selectedPeriod?.documents[0] ?? null);

  // For multi: all docs in the selected period
  const periodDocs = isMulti ? (selectedPeriod?.documents ?? []) : [];

  const handleRemoveWithState = async (doc: Parameters<typeof handleRemoveDocument>[0]) => {
    setRemovingId(doc.id);
    await handleRemoveDocument(doc);
    setRemovingId(null);
  };

  return (
    <div
      ref={containerRef}
      className={
        isHighlighted
          ? `${styles.document_type_container} ${styles.document_type_item_highlighted} ${isFading ? styles.document_type_item_fading : ''}`
          : styles.document_type_container
      }
    >
      {/* Header row */}
      <div className={styles.document_type_item}>
        <div className={styles.document_type_item_title}>
          <div>
            <span role="img" aria-label="document" className={styles.document_type_item_icon}>📄</span>
            <span>
              {type.name}
              {isMulti && ' (Multiple Allowed)'}
              {!isMulti && (type.recurrence || 'none') !== 'none' && ` (${getRecurrenceText(type.recurrence)})`}
            </span>
          </div>

          {!type.required && (
            <label className={styles.fieldTypeItemLabel}>
              <span className={styles.document_type_item_title}>Applicable</span>
              <input
                type="checkbox"
                className={styles.checkboxLg}
                onChange={handleCheck}
                checked={inputEnabled}
              />
            </label>
          )}

          {showReminderIndicator && (
            <span
              className={styles.reminderIndicator}
              title="You have a reminder for this document type"
              aria-label="You have a reminder for this document type"
            >
              <span role="img" aria-label="Reminder">🔔</span>
            </span>
          )}
        </div>

        {showSendReminderButton && (
          <button
            type="button"
            className={styles.reminderButton}
            onClick={() => handleSendReminder(type.id)}
            disabled={processing || reminderJustSent || reminderSentToday || !inputEnabled}
            aria-label={reminderSentToday ? 'Reminder already sent today' : 'Send reminder to agent to upload missing or expired document'}
            title={reminderSentToday ? 'Reminder already sent today' : 'Send reminder to agent to upload missing or expired document'}
          >
            <span role="img" aria-label="Send Reminder">🔔</span>
          </button>
        )}

        {showReminderSentIndicator && (
          <span
            className={styles.reminderSentIndicator}
            title="A reminder has been sent for this document"
            aria-label="A reminder has been sent for this document"
          >
            <span role="img" aria-label="Reminder Sent">✓</span> Reminder Sent
          </span>
        )}

        {/* Non-multi: remove button for any period's document */}
        {!isMulti && selectedDocument && canEdit && (
          <button
            type="button"
            disabled={processing || !inputEnabled}
            onClick={() => handleRemoveWithState(selectedDocument)}
            className={buttonStyles.button + ' ' + buttonStyles.docRemove}
          >
            {processing ? 'Removing...' : 'Remove'}
          </button>
        )}
      </div>

      {/* Body */}
      <div className={styles.fullWidth}>
        {/* Period selector (only shown for recurring types with >1 slot) */}
        <DocumentPeriodSelector
          type={type}
          periods={periods}
          selectedPeriodKey={selectedPeriodKey}
          onSelectionChange={handlePeriodChange}
        />

        {/* Non-multi: document viewer */}
        {!isMulti && (
          <DocumentViewer
            document={selectedDocument}
            isExpired={isDocumentExpired(selectedDocument)}
            handleDocumentDownload={documentDownload}
          />
        )}

        {/* Multi: list of documents in the selected period */}
        {isMulti && (
          <div className={styles.multiDocumentList}>
            {periodDocs.length === 0 && (
              <div className={styles.noDocuments}>No documents uploaded yet.</div>
            )}
            {periodDocs.map(doc => (
              <div key={doc.id} className={styles.multiDocumentItem}>
                <span className={styles.docTitle}>{doc.title || 'Untitled'}</span>
                {isDocumentExpired(doc) && (
                  <span className={styles.expiredTag}>Expired</span>
                )}
                <span className={styles.docDate}>
                  Uploaded: {new Date(doc.createdAt).toLocaleDateString()}
                </span>
                <div className={styles.multiDocumentActions}>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonStyles.button + ' ' + buttonStyles.docView}
                  >
                    View
                  </a>
                  {canEdit && (
                    <button
                      type="button"
                      className={buttonStyles.button + ' ' + buttonStyles.docRemove}
                      disabled={removingId === doc.id}
                      onClick={() => handleRemoveWithState(doc)}
                    >
                      {removingId === doc.id ? 'Removing...' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {template && (
          <button
            type="button"
            onClick={() => handleTemplateDownload(type.id, selectedUser?.id)}
            className={buttonStyles.button + ' ' + buttonStyles.template}
            title={`Download ${template.templateName} template`}
          >
            📥 Download Template
          </button>
        )}

        {showUploadButton && canEdit && (
          <button
            type="button"
            disabled={processing || !inputEnabled}
            onClick={() => setShowUploadForm(true)}
            className={buttonStyles.button + ' ' + buttonStyles.upload}
          >
            {isMulti ? '+ Add Document' : 'Upload'}
          </button>
        )}
      </div>

      {showUploadForm && canEdit && (
        <DocumentUploadForm
          type={type}
          onSuccess={() => setShowUploadForm(false)}
          onCancel={() => setShowUploadForm(false)}
          onUpload={handleUploadFormData}
          isCompanyMode={companyMode}
        />
      )}
    </div>
  );
});

DocumentTypeItem.displayName = 'DocumentTypeItem';

export default DocumentTypeItem;

